import { useQuery } from "@tanstack/react-query";

/**
 * Public channel rate payload, normalized from the backend `Result<ChannelRateVo>`
 * envelope returned by `GET /channel/rate` (anon/public since aps-boot #216).
 *
 * Backend `ChannelRateVo`:
 *   - rate          effective RUB-per-CNY shown to the customer (baseRate × (1 + markupPercent/100))
 *   - baseRate      ЦБ РФ reference rate before markup
 *   - markupPercent operator margin applied on top of baseRate
 *   - snapshotAt    ISO timestamp the rate was captured
 */
export interface ExchangeRate {
  /** Effective RUB per 1 CNY (¥) used for dual-display and checkout conversion. */
  rubPerYuan: number;
  /** ЦБ РФ reference rate before operator markup, when the backend provides it. */
  baseRate?: number;
  /** Operator markup percentage applied on top of baseRate, when provided. */
  markupPercent?: number;
  /** ISO timestamp the rate snapshot was taken. */
  asOf: string;
}

interface ChannelRateEnvelope {
  code?: number | string;
  success?: boolean;
  message?: string;
  data?: ChannelRateVo;
  result?: ChannelRateVo;
}

interface ChannelRateVo {
  rate?: number;
  baseRate?: number;
  markupPercent?: number;
  snapshotAt?: string;
}

/**
 * Reads the public channel exchange rate from `GET /channel/rate`.
 *
 * No auth header is required — the endpoint is anonymous/public (aps-boot #216).
 * On any failure the query surfaces `isError` (no silent hardcoded fallback);
 * consumers decide how to degrade the UI.
 */
export function useExchangeRate(apiBaseUrl: string) {
  return useQuery<ExchangeRate>({
    queryKey: ["money", "rate", "rub-cny"],
    queryFn: () => fetchChannelRate(apiBaseUrl),
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export async function fetchChannelRate(apiBaseUrl: string): Promise<ExchangeRate> {
  const res = await fetch(`${apiBaseUrl}/channel/rate`);
  if (!res.ok) throw new Error(`Rate fetch failed: ${res.status}`);

  const json = (await res.json()) as ChannelRateEnvelope;
  const vo = json.data ?? json.result;
  const rate = vo?.rate;
  if (typeof rate !== "number" || rate <= 0) {
    throw new Error("Invalid rate payload: missing or non-positive rate");
  }

  return {
    rubPerYuan: rate,
    ...(typeof vo?.baseRate === "number" ? { baseRate: vo.baseRate } : {}),
    ...(typeof vo?.markupPercent === "number" ? { markupPercent: vo.markupPercent } : {}),
    asOf: vo?.snapshotAt ?? new Date().toISOString(),
  };
}
